import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  UsersIcon,
  CogIcon,
  ClockIcon,
  XMarkIcon,
  CalendarIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isCoach, getUserRole } from '../utils/roles';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, requiresAdmin: false, requiresCoach: false },
  {
    name: 'Availability',
    href: '/availability',
    icon: ClockIcon,
    requiresAdmin: false,
    requiresCoach: false,
  },
  {
    name: 'Training Sessions',
    href: '/training-sessions',
    icon: CalendarDaysIcon,
    requiresAdmin: false,
    requiresCoach: false,
  },
  {
    name: 'Planner',
    href: '/planner',
    icon: CalendarIcon,
    requiresAdmin: false,
    requiresCoach: true,
  }, // Only admins and coaches can plan
  {
    name: 'Autoplanner',
    href: '/autoplanner',
    icon: SparklesIcon,
    requiresAdmin: false,
    requiresCoach: true,
  }, // Only admins and coaches can use autoplanner
  {
    name: 'Equipment',
    href: '/equipment',
    icon: CogIcon,
    requiresAdmin: true,
    requiresCoach: false,
    hide: document.location.hostname !== 'localhost',
  }, // Only admins manage equipment
  {
    name: 'Crews',
    href: '/crews',
    icon: UserGroupIcon,
    requiresAdmin: false,
    requiresCoach: false,
  }, // All users can view their crews
  {
    name: 'Training Groups',
    href: '/training-groups',
    icon: UsersIcon,
    requiresAdmin: false,
    requiresCoach: false,
  }, // All users can view their training groups
  {
    name: 'Help Center',
    href: '/help',
    icon: QuestionMarkCircleIcon,
    requiresAdmin: false,
    requiresCoach: false,
  },
  {
    name: 'Admin',
    href: '/admin',
    icon: ShieldCheckIcon,
    requiresAdmin: true,
    requiresCoach: false,
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Sidebar = ({ setSidebarOpen }) => {
  const { user } = useAuth();

  const handleNavClick = () => {
    if (setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  // Check if user has coach role in any training group
  const userIsCoach = isCoach(user);

  // Check if user should have access to a navigation item
  const hasAccess = (item) => {
    if (item.requiresAdmin && !isAdmin(user)) {
      return false;
    }
    if (item.requiresCoach && !isAdmin(user) && !userIsCoach) {
      return false;
    }
    if (item.hide) {
      return false;
    }
    return true;
  };

  return (
    <div className="flex h-0 flex-1 flex-col border-r border-gray-200 bg-white">
      <div className="flex flex-1 flex-col overflow-y-auto pb-4 pt-5">
        <div className="flex flex-shrink-0 items-center px-4">
          {setSidebarOpen && (
            <button
              type="button"
              className="rounded-md text-gray-300 hover:bg-gray-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-white md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          )}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rowing-600">
                <span className="text-sm font-bold text-white">RP</span>
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Rowing Planner</h1>
            </div>
          </div>
        </div>

        <nav className="mt-8 flex-1 space-y-1 px-2">
          {navigation
            .filter((item) => hasAccess(item))
            .map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  classNames(
                    isActive
                      ? 'bg-rowing-100 text-rowing-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                    'group flex items-center rounded-md px-2 py-2 text-sm font-medium'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      className={classNames(
                        isActive ? 'text-rowing-500' : 'text-gray-400 group-hover:text-gray-500',
                        'mr-3 h-6 w-6 flex-shrink-0'
                      )}
                      aria-hidden="true"
                    />
                    {item.name}
                  </>
                )}
              </NavLink>
            ))}
        </nav>
      </div>

      <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300">
              <span className="text-sm font-medium text-gray-700">
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-700">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs font-medium capitalize text-gray-500">{getUserRole(user)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
